import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

/**
 * AuthProvider manages the entire authentication state for the app.
 * 
 * - Stores the access token in memory (NOT localStorage) for security
 * - The refresh token lives in an httpOnly cookie (managed by the browser)
 * - Provides login, logout, Google SSO, and silent refresh functions
 * - Auto-refreshes the access token before it expires
 * - On mount, attempts to restore the session from an existing refresh cookie
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // True during initial session check
  const refreshTimerRef = useRef(null);

  const isAuthenticated = !!accessToken && !!user;

  // -- Helper: Parse JWT to get expiry --
  const getTokenExpiry = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch {
      return null;
    }
  };

  // -- Helper: Schedule auto-refresh 2 minutes before expiry --
  const scheduleRefresh = useCallback((token) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    const refreshIn = expiry - Date.now() - (2 * 60 * 1000); // 2 min before expiry
    if (refreshIn <= 0) return; // Token is already about to expire

    refreshTimerRef.current = setTimeout(async () => {
      try {
        await refreshAccessToken();
      } catch {
        // Silently ignore transient refresh errors from the background timer.
        // Network blips and cold Cloud Run starts should NOT log the user out.
        // The next real API call will hit the 401 interceptor, which handles
        // retry and only logs out on a genuine server-side token rejection.
      }
    }, refreshIn);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Set auth state and schedule refresh --
  const setAuthState = useCallback((token, userData) => {
    setAccessToken(token);
    setUser(userData);

    // Make the token available to the API interceptor
    api._setAccessToken(token);

    if (token) {
      scheduleRefresh(token);
    }
  }, [scheduleRefresh]);

  // -- Handle auth failure (clear state, redirect) --
  const handleAuthFailure = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    api._setAccessToken(null);

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
  }, []);

  // -- Login with email + password --
  const login = async (email, password) => {
    const response = await api.post('/login', { email, password });
    const { access_token, user: userData } = response.data;
    setAuthState(access_token, userData);
    return userData;
  };

  // -- Signup with email + password --
  const signup = async (signupData) => {
    const response = await api.post('/signup', signupData);
    return response.data;
  };

  // -- Login with Google (redirect to backend) --
  const loginWithGoogle = () => {
    window.location.href = `${api.defaults.baseURL}/auth/google/login`;
  };

  // -- Handle the callback after Google SSO --
  const handleAuthCallback = async (token) => {
    setAccessToken(token);
    api._setAccessToken(token);

    // Hydrate user data from /auth/me
    const response = await api.get('/auth/me');
    const userData = response.data;
    setAuthState(token, userData);
    return userData;
  };

  // -- Refresh the access token using the httpOnly cookie --
  const refreshAccessToken = async () => {
    const response = await api.post('/auth/refresh');
    const { access_token, user: userData } = response.data;
    setAuthState(access_token, userData);
    return access_token;
  };

  // -- Log in user using an existing token/userData received from backend --
  const loginWithToken = (token, userData) => {
    setAuthState(token, userData);
  };

  // -- Logout --
  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Even if the server call fails, clear local state
    }
    handleAuthFailure();
  };

  // -- On mount: try to restore session from refresh cookie --
  useEffect(() => {
    const restoreSession = async () => {
      // Skip cookie-based restore on the Google SSO callback page.
      // The token arrives via URL param and handleAuthCallback() sets it.
      // Running restoreSession concurrently causes a race: the cookie may
      // not yet be stored, returning 401 and clearing the token that
      // handleAuthCallback just set, resulting in a spurious logout.
      if (window.location.pathname === '/auth/callback') {
        setIsLoading(false);
        return;
      }

      try {
        await refreshAccessToken();
      } catch {
        // No valid refresh cookie — user needs to login
        handleAuthFailure();
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Expose refresh function to api.js for 401 retry --
  useEffect(() => {
    api._refreshAccessToken = refreshAccessToken;
    api._handleAuthFailure = handleAuthFailure;
  }, [handleAuthFailure]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    login,
    signup,
    loginWithGoogle,
    handleAuthCallback,
    refreshAccessToken,
    logout,
    loginWithToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth state and actions from any component.
 * 
 * Usage:
 *   const { user, login, logout, isAuthenticated } = useAuth();
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
